'use client'

import { useEffect, useRef } from 'react'
import {
  ReactCompareSlider,
  ReactCompareSliderHandle,
  ReactCompareSliderImage,
} from 'react-compare-slider'

import type { ApartmentGalleryComparisonItem } from '@/lib/apartments/apartmentGallery'
import { cn } from '@/utilities/ui'

export type RoomGalleryCopy = {
  afterLabel: string
  beforeLabel: string
}

const COMPARISON_HANDLE_BUTTON_STYLE = {
  backdropFilter: 'none',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  border: '1px solid rgba(255, 255, 255, 0.95)',
  boxShadow: 'none',
  height: '2.25rem',
  WebkitBackdropFilter: 'none',
  width: '2.25rem',
} as const

type RoomGalleryComparisonSlideProps = {
  afterLabel: string
  beforeLabel: string
  className?: string
  item: ApartmentGalleryComparisonItem
  labelPlacement?: 'overlay' | 'below-controls'
  objectFit?: 'cover' | 'contain'
}

export function RoomGalleryComparisonSlide({
  afterLabel,
  beforeLabel,
  className,
  item,
  labelPlacement = 'overlay',
  objectFit = 'cover',
}: RoomGalleryComparisonSlideProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const fitClassName = objectFit === 'contain' ? 'object-contain' : 'object-cover'

  useEffect(() => {
    const handleRoot = rootRef.current?.querySelector('[data-rcs="handle-root"]')

    if (!(handleRoot instanceof HTMLElement)) return

    const setFocused = (focused: boolean) => {
      if (focused) {
        handleRoot.dataset.focused = 'true'
        return
      }

      delete handleRoot.dataset.focused
    }

    const onFocus = () => setFocused(true)
    const onBlur = () => setFocused(false)

    handleRoot.addEventListener('focus', onFocus)
    handleRoot.addEventListener('blur', onBlur)

    if (document.activeElement === handleRoot) {
      setFocused(true)
    }

    return () => {
      handleRoot.removeEventListener('focus', onFocus)
      handleRoot.removeEventListener('blur', onBlur)
    }
  }, [item.id])

  return (
    <div
      ref={rootRef}
      className={cn('relative size-full min-h-0', className)}
      data-gallery-interactive=""
      data-room-gallery-comparison=""
    >
      <ReactCompareSlider
        boundsPadding="0px"
        className="size-full min-h-0"
        handle={
          <ReactCompareSliderHandle
            buttonStyle={COMPARISON_HANDLE_BUTTON_STYLE}
            linesStyle={{
              boxShadow: '0 0 4px rgba(0, 0, 0, 0.45)',
              outline: '1px solid rgba(255, 255, 255, 0.95)',
              outlineOffset: -1,
            }}
          />
        }
        itemOne={
          <ReactCompareSliderImage
            alt={item.before.alt}
            className={cn('size-full', fitClassName, 'object-center')}
            src={item.before.src}
          />
        }
        itemTwo={
          <ReactCompareSliderImage
            alt={item.after.alt}
            className={cn('size-full', fitClassName, 'object-center')}
            src={item.after.src}
          />
        }
        keyboardIncrement="5%"
        onlyHandleDraggable={false}
        transition="0.15s ease-out"
      />
      {labelPlacement === 'overlay' ? (
        <div className="pointer-events-none absolute inset-x-0 top-4 z-10 flex justify-between px-4">
          <span className="rounded-full bg-black/70 px-3 py-1 font-outfit text-sm font-medium text-white">
            {beforeLabel}
          </span>
          <span className="rounded-full bg-black/70 px-3 py-1 font-outfit text-sm font-medium text-white">
            {afterLabel}
          </span>
        </div>
      ) : null}
    </div>
  )
}
